import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
// Provide loose env typing inline (alternative to separate env.d.ts)
declare global {
  interface ImportMetaEnv { [key: string]: any }
  interface ImportMeta { env: ImportMetaEnv }
}
import { PublicClientApplication, AccountInfo, SilentRequest, InteractionRequiredAuthError } from '@azure/msal-browser';

const tenantId = import.meta.env.VITE_AAD_TENANT_ID;
const clientId = import.meta.env.VITE_AAD_CLIENT_ID;
const apiScope = import.meta.env.VITE_API_SCOPE || clientId;
const hubFinanceScope = import.meta.env.VITE_HUB_FINANCE_SCOPE || 'api://9d9f4bf2-aabe-4381-9444-ff1119f9309a/Finance.Metric.Read';

// Log scope for debugging
if (import.meta.env.DEV) {
  console.warn(`MSAL Config: tenantId=${tenantId || '(not set)'}, clientId=${clientId || '(not set)'}, apiScope=${apiScope || '(not set)'}, msalEnabled=${!!(clientId && tenantId)}`);
}

const msalInstance = (clientId && tenantId) ? new PublicClientApplication({
  auth: { 
    clientId, 
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri: import.meta.env.DEV ? 'http://localhost:5173' : window.location.origin,
    knownAuthorities: [`login.microsoftonline.com`]
  },
  cache: { cacheLocation: 'localStorage' },
  system: {
    allowNativeBroker: false,
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        console.log(`MSAL [${level}]:`, message);
      },
      logLevel: 3 // Verbose
    }
  }
}) : undefined;

// Track MSAL initialization — store promise to avoid duplicate init calls
let _msalReady: Promise<void> | null = null;

function ensureMsalReady(): Promise<void> {
  if (!msalInstance) return Promise.resolve();
  if (!_msalReady) {
    _msalReady = msalInstance.initialize().catch(e => {
      console.error('MSAL initialize failed:', e);
      _msalReady = null; // allow retry
    });
  }
  return _msalReady;
}

// Handle redirect ONCE at module level, BEFORE React mounts.
// This prevents React 18 Strict Mode from consuming the redirect result on a
// cancelled first effect run and losing it on the second.
import type { AuthenticationResult } from '@azure/msal-browser';
let _redirectPromise: Promise<AuthenticationResult | null> | null = null;

function getRedirectResult(): Promise<AuthenticationResult | null> {
  if (!msalInstance) return Promise.resolve(null);
  if (!_redirectPromise) {
    _redirectPromise = ensureMsalReady().then(() =>
      msalInstance!.handleRedirectPromise()
    ).catch(e => {
      console.error('handleRedirectPromise failed:', e);
      return null;
    });
  }
  return _redirectPromise;
}

// Kick off early (before React renders)
getRedirectResult();

interface AuthContextValue {
  account: AccountInfo | null;
  token: string | null;
  isManager: boolean;
  isAdmin: boolean;
  realAdmin: boolean;
  login: () => Promise<void>;
  logout: () => void;
  loading: boolean;
  user: { upn: string; displayName: string; roles: string[] } | null;
  profileLoading: boolean;
  refreshToken: () => Promise<string | null>;
  getHubToken: () => Promise<{ token: string | null; needsConsent: boolean }>;
  requestHubConsent: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [profileLoading, setProfileLoading] = useState<boolean>(true); // Start as true to wait for initial load
  const [isManager, setIsManager] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [realAdmin, setRealAdmin] = useState<boolean>(false);
  const [user, setUser] = useState<{ upn: string; displayName: string; roles: string[] } | null>(null);

  // On mount: consume the module-level redirect result, pick account
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!msalInstance) { setProfileLoading(false); return; }

      // getRedirectResult() is idempotent — always returns the same promise,
      // so React strict mode double-firing this effect is harmless.
      const result = await getRedirectResult();
      if (cancelled) return;

      if (result?.account) {
        console.log('Redirect login successful:', result.account.username);
        console.log('Redirect accessToken present:', !!result.accessToken);
        console.log('Redirect scopes:', result.scopes);
        setAccount(result.account);
        if (result.accessToken) {
          console.log('Using access token from redirect result');
          setToken(result.accessToken);
          return; // token is set — useEffect[token] will fetch profile
        }
        // If no token in redirect result, acquireTokenSilent fires via account change
      } else {
        // No redirect result — pick any cached account
        await ensureMsalReady();
        if (cancelled) return;
        const accts = msalInstance.getAllAccounts();
        console.log('No redirect result, cached accounts:', accts.length);
        if (accts.length > 0) {
          setAccount(accts[0]);
        } else {
          // Not logged in — stop the loading spinner so Login button shows
          setProfileLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const acquire = useCallback(async () => {
    if (!msalInstance || !account) {
      console.log('Acquire skipped: msalInstance=', !!msalInstance, 'account=', !!account);
      return;
    }
    const req: SilentRequest = { account, scopes: [apiScope] };
    console.log('Acquiring token silently for account:', account.username);
    try {
      const resp = await msalInstance.acquireTokenSilent(req);
      console.log('Token acquired silently');
      setToken(resp.accessToken);
    } catch (e) {
      console.error('Token acquisition failed:', e);
      if (e instanceof InteractionRequiredAuthError) {
        console.log('Interaction required, redirecting for token...');
        try {
          await msalInstance.acquireTokenRedirect(req);
          // Page will redirect; execution stops here
        } catch (redirectErr) {
          console.error('Redirect auth failed:', redirectErr);
        }
      }
    }
  }, [account, apiScope, msalInstance]);

  // Force-refresh token (used by API client on 401). Returns new token or null.
  const refreshToken = useCallback(async (): Promise<string | null> => {
    if (!msalInstance || !account) return null;
    const req: SilentRequest = { account, scopes: [apiScope], forceRefresh: true };
    console.log('refreshToken: forcing silent re-acquisition for', account.username);
    try {
      const resp = await msalInstance.acquireTokenSilent(req);
      console.log('refreshToken: got new token');
      setToken(resp.accessToken);
      return resp.accessToken;
    } catch (e) {
      console.error('refreshToken failed:', e);
      if (e instanceof InteractionRequiredAuthError) {
        console.log('refreshToken: interaction required, redirecting to login...');
        try {
          await msalInstance.acquireTokenRedirect({ scopes: [apiScope] });
        } catch (redirectErr) {
          console.error('refreshToken redirect failed:', redirectErr);
        }
      }
      return null;
    }
  }, [account, apiScope, msalInstance]);

  /** Silent-only Hub token (different Entra audience). Never redirect here — that hangs queries. */
  const getHubToken = useCallback(async (): Promise<{ token: string | null; needsConsent: boolean }> => {
    if (!msalInstance || !account) return { token: null, needsConsent: false };
    const req: SilentRequest = { account, scopes: [hubFinanceScope] };
    try {
      const silent = msalInstance.acquireTokenSilent(req);
      const resp = await Promise.race([
        silent,
        new Promise<never>((_, reject) => setTimeout(() => reject(new Error('HUB_TOKEN_TIMEOUT')), 8000)),
      ]);
      return { token: resp.accessToken, needsConsent: false };
    } catch (e) {
      console.error('getHubToken failed:', e);
      const needsConsent = e instanceof InteractionRequiredAuthError
        || (e instanceof Error && /interaction_required|consent_required|login_required|HUB_TOKEN_TIMEOUT/i.test(e.message));
      return { token: null, needsConsent };
    }
  }, [account, msalInstance]);

  const requestHubConsent = useCallback(async () => {
    if (!msalInstance) return;
    await ensureMsalReady();
    try {
      await msalInstance.acquireTokenRedirect({
        scopes: [hubFinanceScope],
        account: account || undefined,
      });
    } catch (e) {
      console.error('requestHubConsent failed:', e);
    }
  }, [account, msalInstance]);

  const fetchProfile = useCallback(async () => {
    setProfileLoading(true);
    try {
      // Use VITE_API_BASE when set (production), otherwise empty for proxy
      const base = import.meta.env.VITE_API_BASE || '';
      const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
      // Admin "View as" role override
      const roleOverride = localStorage.getItem('roleOverride') || '';
      if (roleOverride && roleOverride !== 'admin') headers['X-Role-Override'] = roleOverride;
      console.log('Fetching profile, token present:', !!token);
      const r = await fetch(`${base}/employees/me`, { headers });
      if (r.ok) {
        const data = await r.json();
        console.log('Profile data:', data);
        setIsManager(!!data.isManager);
        setIsAdmin(!!data.isAdmin);
        setRealAdmin(!!data._realAdmin);
        setUser({
          upn: data.upn || 'demo@example.com',
          displayName: data.displayName || 'Demo User',
          roles: data.roles || ['user']
        });
      } else {
        const errorText = await r.text();
        console.error('Profile fetch failed:', r.status, errorText);
        setIsManager(false); 
        setIsAdmin(false);
        setRealAdmin(false);
        setUser(null);
      }
    } catch (e) {
      console.error('Profile fetch error:', e);
      setIsManager(false); 
      setIsAdmin(false);
      setRealAdmin(false);
      setUser(null);
    } finally {
      setProfileLoading(false);
    }
  }, [token]);

  const login = async () => {
    if (!msalInstance) {
      alert('Login is not configured for this deployment (missing AAD client/tenant settings).');
      return;
    }
    
    // Ensure MSAL is initialized
    await ensureMsalReady();
    
    setLoading(true);
    try {
      console.log('Attempting login with scope:', apiScope);
      // Use redirect flow to avoid COOP popup issues with Vite HTTPS
      await msalInstance.loginRedirect({ 
        scopes: [apiScope],  // Hub Finance.Metric.Read acquired separately (cannot mix with .default)
        prompt: 'select_account'
      });
      // Page will redirect — code below won't execute until return
    } catch (error: any) {
      console.error('Login failed:', error);
      // Handle interaction_in_progress error
      if (error.errorCode === 'interaction_in_progress') {
        // Clear stale interaction state
        sessionStorage.clear();
        alert('A previous login was interrupted. Please try again.');
      } else {
        alert(`Login failed: ${error.message || error.errorCode || 'Unknown error'}\n\nCheck browser console for details.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    if (!msalInstance) {
      setAccount(null); 
      setToken(null); 
      setUser(null);
      setIsManager(false);
      setIsAdmin(false);
      setRealAdmin(false);
      return;
    }
    msalInstance.logoutRedirect();
  };

  // Initialize: unauth dev mode
  useEffect(() => {
    if (!msalInstance) {
      // In unauth dev mode, fetch profile immediately
      fetchProfile();
    }
    // Account picking now handled in the redirect-aware mount effect above
  }, []);

  // Token acquisition when account changes
  useEffect(() => { 
    if (account && msalInstance) {
      console.log('Account changed, triggering token acquisition for:', account.username);
      acquire(); 
    }
  }, [account, acquire, msalInstance]);

  // Fetch profile after token or on startup for unauth mode
  useEffect(() => { 
    // Only fetch if we have a token (or in unauth dev mode)
    if (token || !msalInstance) {
      fetchProfile(); 
    }
  }, [token, fetchProfile]);

  return (
    <AuthContext.Provider value={{ account, token, isManager, isAdmin, realAdmin, login, logout, loading, user, profileLoading, refreshToken, getHubToken, requestHubConsent }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
